import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import {
  ConnectedAppAccessEvent,
  EventBatch,
  GetConnectedAppActivityResponse,
  GetConnectedAppActivitySummaryResponse,
  batchEvents,
  countDistinct,
  describeContext,
  providerDisplayName,
  providerName,
} from './connectedAppActivityTypes';
import './connected-app-activity-view.scss';

/**
 * Connected-app access ledger (DRE-322): when the assistant used each connected
 * app, what it did (plain English) and why. Rendered as a detail page inside
 * the Connected Apps section, behind the "See how … has used your apps" row.
 * Layout follows Figma 5219:9281 (activity) / 5219:8844 (empty).
 */

type Period = '7d' | '30d' | '90d' | 'all';

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

const PAGE_SIZE = 25;

const periodStart = (period: Period): string | undefined => {
  if (period === 'all') return undefined;
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
};

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(undefined, TIME_FORMAT);

/** "11:31–12:05" (or "11:31 – 12:05 PM" in 12h locales) via Intl.formatRange. */
const formatTimeRange = (fromIso: string, toIso: string): string => {
  const formatter = new Intl.DateTimeFormat(undefined, TIME_FORMAT);
  return typeof formatter.formatRange === 'function'
    ? formatter.formatRange(new Date(fromIso), new Date(toIso))
    : `${formatTime(fromIso)}\u2013${formatTime(toIso)}`;
};

const MAX_BATCH_SUMMARIES = 3;

/** "Searched your inbox ×3 · Read an email · +2 more" for a collapsed batch. */
const batchSummary = (events: ConnectedAppAccessEvent[]): string => {
  const distinct = countDistinct(events.map((e) => e.summary));
  const shown = distinct
    .slice(0, MAX_BATCH_SUMMARIES)
    .map(({ value, count }) => (count > 1 ? `${value} \u00d7${count}` : value));
  const hidden = distinct.length - shown.length;
  if (hidden > 0) shown.push(`+${hidden} more`);
  return shown.join(' \u00b7 ');
};

const batchContext = (
  events: ConnectedAppAccessEvent[],
): { task: string | null; source: string } => {
  const contexts = events.map(describeContext);
  const tasks = countDistinct(
    contexts.flatMap((c) => (c.task ? [c.task] : [])),
  );
  const sources = countDistinct(contexts.map((c) => c.source));
  const task =
    tasks.length === 0
      ? null
      : tasks.length === 1
        ? tasks[0].value
        : `${tasks[0].value} and ${tasks.length - 1} other ${
            tasks.length === 2 ? 'task' : 'tasks'
          }`;
  return { task, source: sources.map((s) => s.value).join(' \u00b7 ') };
};

const dayKey = (iso: string): string => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
};

const ArrowLeftIcon: FC = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <path
      d='M12.6667 8H3.33333M8 3.33333L3.33333 8L8 12.6667'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const DownloadIcon: FC = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <path
      d='M14 10V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V10M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ChevronDownIcon: FC = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <path
      d='M4 6L8 10L12 6'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ActivityIcon: FC = () => (
  <svg width='32' height='32' viewBox='0 0 32 32' fill='none'>
    <path
      d='M29.3333 16H24L20 28L12 4L8 16H2.66667'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ProviderBadge: FC<{
  provider: string;
  icon?: React.ReactNode;
}> = ({ provider, icon }) =>
  icon ? (
    <span className='caa-provider-icon'>{icon}</span>
  ) : (
    <span className='caa-provider-initial' aria-hidden='true'>
      {providerName(provider).name.charAt(0)}
    </span>
  );

const ProviderLabel: FC<{ provider: string }> = ({ provider }) => {
  const { name, detail } = providerName(provider);
  return (
    <>
      <strong>{name}</strong>
      {detail && <span className='caa-provider-detail'> ({detail})</span>}
    </>
  );
};

export interface ConnectedAppActivityViewProps {
  onBack: () => void;
  assistantName: string;
  /** Provider code (e.g. GOOGLE, LINEAR) → logo, reused from the hub list. */
  providerIcons: Record<string, React.ReactNode>;
}

const ConnectedAppActivityView: FC<ConnectedAppActivityViewProps> = ({
  onBack,
  assistantName,
  providerIcons,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const [summary, setSummary] =
    useState<GetConnectedAppActivitySummaryResponse | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [events, setEvents] = useState<ConnectedAppAccessEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const [seenProviders, setSeenProviders] = useState<string[]>([]);
  const [hasAnyHistory, setHasAnyHistory] = useState<boolean | null>(null);

  const [provider, setProvider] = useState<string>('all');
  const [period, setPeriod] = useState<Period>('30d');
  const [exporting, setExporting] = useState(false);
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleBatch = useCallback((id: string) => {
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const fetchSummary = useCallback(async () => {
    setSummaryError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetConnectedAppActivitySummaryResponse>(
        '/connected-app-activity/summary',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSummary(res.data);
    } catch (err) {
      console.error('Error fetching connected-app activity summary:', err);
      setSummaryError('Could not load your usage summary.');
    }
  }, [getAccessTokenSilently]);

  const fetchHistoryFlag = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetConnectedAppActivityResponse>(
        '/connected-app-activity',
        { params: { limit: 1 }, headers: { Authorization: `Bearer ${token}` } },
      );
      setHasAnyHistory(res.data.events.length > 0);
    } catch (err) {
      console.error('Error checking connected-app activity history:', err);
      setHasAnyHistory(true);
    }
  }, [getAccessTokenSilently]);

  const fetchEvents = useCallback(
    async (cursor: string | null) => {
      const append = cursor !== null;
      if (append) setLoadingMore(true);
      else setEventsLoading(true);
      setEventsError(null);
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetConnectedAppActivityResponse>(
          '/connected-app-activity',
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              provider: provider === 'all' ? undefined : provider,
              from: periodStart(period),
              cursor: cursor ?? undefined,
              limit: PAGE_SIZE,
            },
          },
        );
        setEvents((prev) =>
          append ? [...prev, ...res.data.events] : res.data.events,
        );
        setSeenProviders((prev) => {
          const next = new Set(prev);
          res.data.events.forEach((e) => next.add(e.provider));
          return next.size === prev.length ? prev : Array.from(next);
        });
        setNextCursor(res.data.nextCursor);
      } catch (err) {
        console.error('Error fetching connected-app activity:', err);
        setEventsError(
          `Could not load how ${assistantName} has used your apps. Please try again.`,
        );
      } finally {
        setEventsLoading(false);
        setLoadingMore(false);
      }
    },
    [getAccessTokenSilently, provider, period, assistantName],
  );

  useEffect(() => {
    fetchSummary();
    fetchHistoryFlag();
  }, [fetchSummary, fetchHistoryFlag]);

  useEffect(() => {
    fetchEvents(null);
  }, [fetchEvents]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<Blob>('/connected-app-activity/export.csv', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          provider: provider === 'all' ? undefined : provider,
          from: periodStart(period),
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `connected-app-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting connected-app activity:', err);
      setEventsError('Could not export your activity. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [getAccessTokenSilently, provider, period]);

  const providerOptions = useMemo(() => {
    const codes = new Set<string>();
    summary?.perProvider.forEach((p) => codes.add(p.provider));
    seenProviders.forEach((code) => codes.add(code));
    if (provider !== 'all') codes.add(provider);
    return Array.from(codes).sort((a, b) =>
      providerDisplayName(a).localeCompare(providerDisplayName(b)),
    );
  }, [summary, seenProviders, provider]);

  const groupedEvents = useMemo(() => {
    const groups: { day: string; events: ConnectedAppAccessEvent[] }[] = [];
    for (const event of events) {
      const day = dayKey(event.createdAt);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.events.push(event);
      else groups.push({ day, events: [event] });
    }
    return groups.map((group) => ({
      day: group.day,
      batches: batchEvents(group.events),
    }));
  }, [events]);

  const neverUsed =
    hasAnyHistory === false &&
    !eventsLoading &&
    !eventsError &&
    events.length === 0 &&
    provider === 'all';

  const renderHero = () => {
    if (summaryError) {
      return (
        <div className='caa-inline-error' role='alert'>
          {summaryError}{' '}
          <button className='caa-link-btn' onClick={fetchSummary}>
            Retry
          </button>
        </div>
      );
    }
    const mostUsed = summary?.mostUsed ?? null;
    return (
      <div className='caa-hero' data-testid='caa-hero'>
        <div className='caa-hero-stat'>
          <span className='caa-hero-label'>Connected apps</span>
          <span className='caa-hero-value'>
            {summary ? summary.connectedAppCount : '\u2013'}
          </span>
        </div>
        <div className='caa-hero-stat'>
          <span className='caa-hero-label'>Activity in the last 30d</span>
          <span className='caa-hero-value'>
            {summary ? summary.totalAccesses30d : '\u2013'}
          </span>
        </div>
        <div className='caa-hero-stat'>
          <span className='caa-hero-label'>Most used last 30d</span>
          {mostUsed ? (
            <span className='caa-hero-value caa-hero-most-used'>
              <span>{mostUsed.accessCount30d}</span>
              <span className='caa-hero-app'>
                <ProviderBadge
                  provider={mostUsed.provider}
                  icon={providerIcons[mostUsed.provider]}
                />
                <span className='caa-hero-app-name'>
                  <ProviderLabel provider={mostUsed.provider} />
                </span>
              </span>
            </span>
          ) : (
            <span className='caa-hero-value'>{'\u2013'}</span>
          )}
        </div>
      </div>
    );
  };

  const renderAccount = (accountLabel: string | null) =>
    accountLabel && (
      <span className='caa-event-account'> ({accountLabel})</span>
    );

  const renderWhy = (task: string | null, source: string) => (
    <div className='caa-event-why'>
      {task && <p className='caa-event-task'>{task}</p>}
      <p className='caa-event-source'>{source}</p>
    </div>
  );

  const renderEvent = (event: ConnectedAppAccessEvent, nested = false) => {
    const { task, source } = describeContext(event);
    const classes = ['caa-event'];
    if (event.isDisconnected) classes.push('disconnected');
    if (nested) classes.push('caa-event-nested');
    return (
      <div key={event.id} className={classes.join(' ')} data-testid='caa-event'>
        <span className='caa-event-time'>{formatTime(event.createdAt)}</span>
        <div className='caa-event-main'>
          {!nested && (
            <ProviderBadge
              provider={event.provider}
              icon={providerIcons[event.provider]}
            />
          )}
          <div className='caa-event-body'>
            <div className='caa-event-what'>
              {!nested && (
                <p className='caa-event-app'>
                  <ProviderLabel provider={event.provider} />
                  {event.isDisconnected && (
                    <span className='caa-pill'>App disconnected</span>
                  )}
                </p>
              )}
              <p className='caa-event-summary'>
                {event.summary}
                {renderAccount(event.accountLabel)}
              </p>
            </div>
            {renderWhy(task, source)}
          </div>
        </div>
      </div>
    );
  };

  const renderBatch = (batch: EventBatch) => {
    if (batch.events.length === 1) return renderEvent(batch.events[0]);
    const open = expandedBatches.has(batch.id);
    const { task, source } = batchContext(batch.events);
    const classes = ['caa-batch'];
    if (open) classes.push('open');
    if (batch.isDisconnected) classes.push('disconnected');
    const rowClasses = ['caa-event', 'caa-batch-row'];
    if (batch.isDisconnected) rowClasses.push('disconnected');
    const count = batch.events.length;
    return (
      <div key={batch.id} className={classes.join(' ')} data-testid='caa-batch'>
        <div
          className={rowClasses.join(' ')}
          role='button'
          tabIndex={0}
          aria-expanded={open}
          aria-label={`${count} actions in ${providerDisplayName(batch.provider)}, ${
            open ? 'hide' : 'show'
          } details`}
          onClick={() => toggleBatch(batch.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleBatch(batch.id);
            }
          }}
        >
          <span className='caa-event-time'>
            {formatTimeRange(batch.from, batch.to)}
          </span>
          <div className='caa-event-main'>
            <ProviderBadge
              provider={batch.provider}
              icon={providerIcons[batch.provider]}
            />
            <div className='caa-event-body'>
              <div className='caa-event-what'>
                <p className='caa-event-app'>
                  <ProviderLabel provider={batch.provider} />
                  <span className='caa-pill caa-pill-count'>
                    {count} actions
                  </span>
                  {batch.isDisconnected && (
                    <span className='caa-pill'>App disconnected</span>
                  )}
                </p>
                <p className='caa-event-summary'>
                  {batchSummary(batch.events)}
                  {renderAccount(batch.accountLabel)}
                </p>
              </div>
              {renderWhy(task, source)}
            </div>
            <span className='caa-batch-chevron' aria-hidden='true'>
              <ChevronDownIcon />
            </span>
          </div>
        </div>
        {open && (
          <div className='caa-batch-items'>
            {batch.events.map((event) => renderEvent(event, true))}
          </div>
        )}
      </div>
    );
  };

  const renderTimeline = () => {
    if (eventsLoading)
      return <div className='settings-skeleton caa-skeleton' />;
    if (eventsError) {
      return (
        <div className='caa-state caa-state-error' role='alert'>
          <p>{eventsError}</p>
          <button
            className='caa-secondary-btn'
            onClick={() => fetchEvents(null)}
          >
            Try again
          </button>
        </div>
      );
    }
    if (events.length === 0) {
      return (
        <div className='caa-state'>
          <p className='caa-state-title'>No activity to show</p>
          <p>
            {assistantName} hasn&apos;t used{' '}
            {provider === 'all' ? 'your apps' : providerDisplayName(provider)}{' '}
            {period === 'all' ? 'yet' : 'in this period'}. Try a longer period
            or another app.
          </p>
        </div>
      );
    }
    return (
      <>
        <div className='caa-timeline'>
          {groupedEvents.map((group) => (
            <div key={group.day} className='caa-day'>
              <h4 className='caa-day-title'>{group.day}</h4>
              {group.batches.map(renderBatch)}
            </div>
          ))}
        </div>
        {nextCursor && (
          <div className='caa-load-more'>
            <button
              className='caa-secondary-btn'
              onClick={() => fetchEvents(nextCursor)}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Show more'}
            </button>
          </div>
        )}
      </>
    );
  };

  const renderActivityCard = () => (
    <section className='caa-card' aria-labelledby='caa-card-title'>
      <h3 className='caa-card-title' id='caa-card-title'>
        Your activity
      </h3>
      <div className='caa-toolbar'>
        <div className='caa-filters'>
          <span className='caa-filter-label'>Filter by</span>
          <label className='caa-select-wrap'>
            <select
              className='caa-select'
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              aria-label='Filter by app'
            >
              <option value='all'>All apps</option>
              {providerOptions.map((code) => (
                <option key={code} value={code}>
                  {providerDisplayName(code)}
                </option>
              ))}
            </select>
            <span className='caa-select-chevron'>
              <ChevronDownIcon />
            </span>
          </label>
          <label className='caa-select-wrap'>
            <select
              className='caa-select'
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              aria-label='Filter by period'
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className='caa-select-chevron'>
              <ChevronDownIcon />
            </span>
          </label>
        </div>
        <button
          className='caa-export-btn'
          onClick={handleExport}
          disabled={exporting || eventsLoading || events.length === 0}
          title='Download this activity as a CSV file'
        >
          <DownloadIcon />
          <span>{exporting ? 'Preparing…' : 'Export CSV'}</span>
        </button>
      </div>
      <div className='caa-divider' />
      {renderTimeline()}
    </section>
  );

  const renderNeverUsed = () => (
    <div className='caa-empty' data-testid='caa-empty'>
      <span className='caa-empty-icon'>
        <ActivityIcon />
      </span>
      <p className='caa-empty-title'>No activity to show</p>
      <p className='caa-empty-text'>
        {assistantName} hasn&apos;t used any apps yet. When you ask them to do
        something it&apos;ll show here.
      </p>
    </div>
  );

  return (
    <div className='connected-app-activity-view'>
      <div className='caa-header'>
        <button className='caa-back-btn' onClick={onBack}>
          <ArrowLeftIcon />
          <span>Back</span>
        </button>
        <h2 className='caa-title'>
          How {assistantName}&rsquo;s used your apps
        </h2>
        <p className='caa-subtitle'>
          See which tasks and activities {assistantName}&rsquo;s accessed your
          apps for
        </p>
      </div>

      {renderHero()}

      {neverUsed ? renderNeverUsed() : renderActivityCard()}
    </div>
  );
};

export default ConnectedAppActivityView;
